import {
	context,
	propagation,
	trace,
	SpanKind,
	SpanStatusCode,
	type Span,
	type Tracer,
} from '@opentelemetry/api';

// Observe only explicit API calls. Never capture URLs, headers, bodies, or raw errors.
export function createObservedFetch(
	tracer: Tracer,
	logError: (message: string, fields: Record<string, unknown>) => void,
) {
	return async (
		input: string,
		init?: RequestInit,
		propagationOrigin?: string,
	): Promise<Response> => {
		const requestedMethod = (init?.method || 'GET').toUpperCase();
		const method = [
			'GET',
			'POST',
			'PUT',
			'PATCH',
			'DELETE',
			'HEAD',
			'OPTIONS',
		].includes(requestedMethod)
			? requestedMethod
			: 'OTHER';
		let span: Span | undefined;
		try {
			span = tracer.startSpan(`HTTP ${method}`, {
				kind: SpanKind.CLIENT,
				attributes: { 'http.request.method': method },
			});
		} catch {
			/* Telemetry must not prevent an API request. */
		}
		let requestInit = init;
		try {
			if (
				span &&
				propagationOrigin &&
				new URL(input, globalThis.location?.href).origin ===
					new URL(propagationOrigin, globalThis.location?.href).origin
			) {
				const carrier: Record<string, string> = {};
				propagation.inject(trace.setSpan(context.active(), span), carrier);
				if (Object.keys(carrier).length) {
					const headers = new Headers(init?.headers);
					for (const key of ['traceparent', 'tracestate']) {
						if (carrier[key]) headers.set(key, carrier[key]);
					}
					requestInit = { ...init, headers };
				}
			}
		} catch {
			/* Propagation must not prevent the API request. */
		}
		const failed = (status?: number) => {
			try {
				span?.setStatus({ code: SpanStatusCode.ERROR });
				const spanContext = span?.spanContext();
				const emit = () =>
					logError('HTTP request failed', {
						'http.request.method': method,
						...(status === undefined ? {} : { 'http.response.status_code': status }),
						...(spanContext
							? { traceId: spanContext.traceId, spanId: spanContext.spanId }
							: {}),
					});
				if (span) context.with(trace.setSpan(context.active(), span), emit);
				else emit();
			} catch {
				/* Export failures must not replace the original result. */
			}
		};
		try {
			const response = await fetch(input, requestInit);
			try {
				span?.setAttribute('http.response.status_code', response.status);
			} catch {
				/* Best effort. */
			}
			if (response.status >= 400) failed(response.status);
			return response;
		} catch (error) {
			if (
				!init?.signal?.aborted &&
				!(error instanceof Error && error.name === 'AbortError')
			)
				failed();
			throw error;
		} finally {
			try {
				span?.end();
			} catch {
				/* Best effort. */
			}
		}
	};
}
